import {
  DirectionalLight,
  Mesh,
  Object3D,
  ShadowMaterial,
  Vector3,
} from 'three';

/**
 * Light travels from this direction (world space, not normalized).
 * Matches the profile `key` light at (2.5, 3.5, 2.0) so hull shadows fall
 * forward-left instead of a top-down contact blob.
 */
export const HULL_SUN_DIRECTION = new Vector3(2.5, 3.5, 2.0).normalize();

/** Distance from the foot anchor to the shadow-casting light. */
export const HULL_SUN_DISTANCE_M = 10;

/** Darkening strength on the hull shadow-catcher pass. */
export const HULL_SHADOW_CATCHER_OPACITY = 0.42;

const HULL_SHADOW_CATCHER = 'hullShadowCatcher';

/**
 * MeshBasic hull materials do not run the shadow-map shader chunk in Three.js,
 * so `receiveShadow` on the photo shell has no effect. Duplicate each hull mesh
 * with shared geometry + {@link ShadowMaterial} so cast shadows land on the real surface.
 */
export function attachHullShadowCatchers(hull: Object3D): void {
  detachHullShadowCatchers(hull);

  hull.traverse((node) => {
    const m = node as Mesh;
    if (!m.isMesh) return;
    if (m.userData['isMeshOnlyWire']) return;
    if (m.userData[HULL_SHADOW_CATCHER]) return;

    m.castShadow = false;
    m.receiveShadow = false;

    const mat = new ShadowMaterial({
      opacity: HULL_SHADOW_CATCHER_OPACITY,
      transparent: true,
    });
    mat.depthWrite = false;

    const catcher = new Mesh(m.geometry, mat);
    catcher.userData[HULL_SHADOW_CATCHER] = true;
    catcher.castShadow = false;
    catcher.receiveShadow = true;
    catcher.renderOrder = (m.renderOrder ?? 0) + 2;

    const parent = m.parent;
    if (!parent) return;
    parent.add(catcher);
    catcher.position.copy(m.position);
    catcher.quaternion.copy(m.quaternion);
    catcher.scale.copy(m.scale);
  });
}

export function detachHullShadowCatchers(hull: Object3D): void {
  const remove: Object3D[] = [];
  hull.traverse((node) => {
    if (node.userData[HULL_SHADOW_CATCHER]) remove.push(node);
  });
  for (const node of remove) {
    const mesh = node as Mesh;
    node.parent?.remove(node);
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) mat?.dispose();
  }
}

/** Aim the hull shadow sun at the feet from {@link HULL_SUN_DIRECTION}. */
export function syncHullSunOverFeet(
  light: DirectionalLight,
  footX: number,
  footY: number,
  footZ: number,
): void {
  light.target.position.set(footX, footY, footZ);
  light.position
    .copy(light.target.position)
    .addScaledVector(HULL_SUN_DIRECTION, HULL_SUN_DISTANCE_M);
  light.target.updateMatrixWorld();
}
